import React, { useEffect, useState } from "react";
import { ShieldCheck, Mail } from "lucide-react";

const calcTimeLeft = (target) => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0, launched: true };
    }
    return {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff % 86400000) / 3600000),
        minutes: Math.floor((diff % 3600000) / 60000),
        seconds: Math.floor((diff % 60000) / 1000),
        launched: false,
    };
};

const CountdownUnit = ({ value, label }) => (
    <div className="flex flex-col items-center" data-testid={`countdown-${label.toLowerCase()}`}>
        <div className="font-anton text-4xl md:text-6xl text-white tracking-wide leading-none tabular-nums">
            {String(value).padStart(2, "0")}
        </div>
        <div className="font-chivo text-[9px] md:text-[10px] tracking-[0.32em] uppercase text-[#3b82f6] mt-2">
            {label}
        </div>
    </div>
);

const Countdown = ({ target }) => {
    const [t, setT] = useState(() => calcTimeLeft(target));
    useEffect(() => {
        const id = setInterval(() => setT(calcTimeLeft(target)), 1000);
        return () => clearInterval(id);
    }, [target]);

    if (t.launched) {
        return (
            <div
                data-testid="countdown-launched"
                className="font-anton text-3xl md:text-4xl text-[#22c55e] uppercase tracking-wide"
            >
                We're Live.
            </div>
        );
    }

    return (
        <div
            data-testid="countdown-container"
            className="flex items-start justify-center gap-4 md:gap-8"
        >
            <CountdownUnit value={t.days} label="Days" />
            <div className="font-anton text-3xl md:text-5xl text-[#1E293B] leading-none">:</div>
            <CountdownUnit value={t.hours} label="Hours" />
            <div className="font-anton text-3xl md:text-5xl text-[#1E293B] leading-none">:</div>
            <CountdownUnit value={t.minutes} label="Minutes" />
            <div className="font-anton text-3xl md:text-5xl text-[#1E293B] leading-none">:</div>
            <CountdownUnit value={t.seconds} label="Seconds" />
        </div>
    );
};

const Maintenance = ({ preorderOpens, launchDate }) => {
    // Default targets if props not passed (legacy callers)
    const preorderDate = preorderOpens
        ? new Date(preorderOpens)
        : new Date("2026-06-01T00:00:00Z");
    const shipDate = launchDate
        ? new Date(launchDate)
        : new Date("2026-07-01T00:00:00Z");

    const preorderLabel = preorderDate.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
    const shipLabel = shipDate.toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
        year: "numeric",
    });

    return (
        <div
            data-testid="maintenance-page"
            className="min-h-screen bg-[#0B0E14] text-white relative overflow-hidden flex items-center justify-center px-6 md:px-10"
        >
            {/* Subtle grid backdrop */}
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none"
                style={{
                    backgroundImage:
                        "linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                }}
            />
            {/* Navy glow */}
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#004466] opacity-20 blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#004466] opacity-10 blur-[100px] pointer-events-none" />

            <div className="relative max-w-[680px] w-full text-center">
                {/* Logo */}
                <img
                    src="/bs-logo-v2.png"
                    alt="Badge & Shield Coffee Co."
                    className="h-32 md:h-40 w-auto mx-auto mb-10 object-contain"
                    style={{ filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.6))" }}
                />

                {/* Stencil eyebrow */}
                <div className="flex items-center justify-center gap-3 mb-6">
                    <div className="h-px w-10 bg-[#004466]" />
                    <div className="font-chivo text-[11px] tracking-[0.32em] uppercase text-[#3b82f6]">
                        Off Duty
                    </div>
                    <div className="h-px w-10 bg-[#004466]" />
                </div>

                {/* Headline */}
                <h1 className="font-anton text-5xl md:text-7xl uppercase tracking-wide leading-[0.95]">
                    We're <span className="text-[#ffffff]">Brewing</span>
                    <br />
                    Something Better.
                </h1>

                <p className="font-body text-slate-300 text-base md:text-lg mt-8 max-w-md mx-auto leading-relaxed">
                    Badge &amp; Shield Coffee Co. is briefly offline while we
                    finish pouring the next batch.
                </p>

                {/* Pre-order countdown */}
                <div className="mt-12 mb-2">
                    <div className="font-chivo text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-slate-500 mb-6">
                        Pre-Orders Open {preorderLabel}
                    </div>
                    <Countdown target={preorderDate} />
                    <div className="font-chivo text-[10px] tracking-[0.32em] uppercase text-slate-600 mt-6">
                        Shipping Starts {shipLabel}
                    </div>
                </div>

                {/* Contact */}
                <div className="bs-stencil-divider my-12 max-w-xs mx-auto" />

                <div className="grid grid-cols-1 gap-6 max-w-xs mx-auto">
                    <a
                        href="mailto:badgeandshieldcoffee@gmail.com"
                        data-testid="maintenance-email-link"
                        className="flex flex-col items-center gap-2 p-5 border border-[#1E293B] hover:border-[#004466] transition-colors group"
                    >
                        <Mail className="w-5 h-5 text-[#004466] group-hover:text-white transition-colors" />
                        <div className="font-chivo text-[10px] tracking-[0.22em] uppercase text-slate-500">
                            Email
                        </div>
                        <div className="font-body text-xs text-slate-300 break-all">
                            badgeandshieldcoffee@gmail.com
                        </div>
                    </a>
                </div>

                {/* Motto */}
                <div className="mt-16 flex items-center justify-center gap-3">
                    <ShieldCheck className="w-4 h-4 text-[#004466]" />
                    <div className="font-chivo text-[10px] tracking-[0.36em] uppercase text-slate-500">
                        Duty · Honor · Coffee
                    </div>
                    <ShieldCheck className="w-4 h-4 text-[#004466]" />
                </div>
            </div>
        </div>
    );
};

export default Maintenance;
